import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  approveAcceptanceInspection,
  deleteAcceptanceInspection,
  deleteAcceptanceInspectionAttachment,
  downloadAcceptanceInspectionAttachment,
  downloadAcceptanceInspectionPdf,
  fetchAcceptanceInspection,
  fetchAcceptanceInspections,
  revokeAcceptanceInspection,
  uploadAcceptanceInspectionAttachment,
} from '../../api/acceptanceInspection';
import { fetchCompanyInventory } from '../../api/companyInventory';
import { useCompanyStore } from '../../store/companyStore';
import {
  AcceptanceInspection,
  acceptanceItemLabel,
  acceptanceStatusLabel,
  isMediaAttachment,
  isRemarkAttachment,
} from '../../types/acceptanceInspection';
import { CompanyInventoryItem, formatInventoryQty } from '../../types/companyInventory';
import { APPROVAL_TARGET_ACCEPTANCE_INSPECTION } from '../../types/approval';
import { canConfirmInspection } from '../../types/companyRole';
import {
  buildNcrDefectDesc,
  hasNonconformance,
} from '../../types/nonconformanceReport';
import { PRODUCT_SUB_NCR } from '../layout/Header';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import AcceptanceInspectionCreate from './AcceptanceInspectionCreate';
import AcceptanceInspectionSheet from './AcceptanceInspectionSheet';
import {notifyError, reportError} from '../../utils/notify';
import '../contract/ContractPanel.css';
import '../equipment/EquipTabPanel.css';
import './ProductForm.css';
import './AcceptanceInspectionPanel.css';

interface Props {
  /** 계약 상세/임베드에서 해당 계약 인수만 표시 */
  contractId?: number | null;
  embedded?: boolean;
}

type PanelView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; inspectionId: number }
  | { type: 'detail'; inspectionId: number };

type InventoryFilter = {
  inventoryId: number;
  itemName: string;
  specText: string;
} | null;

const LIST_VIEW: PanelView = { type: 'list' };

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function AcceptanceInspectionPanel({ contractId = null, embedded = false }: Props) {
  const navigate = useNavigate();
  const companyId = useCompanyStore((state) => state.companyId);
  const role = useCompanyStore((state) => state.role);
  const canApproveDirect = canConfirmInspection(role);
  void embedded;
  const [view, setView] = useState<PanelView>(LIST_VIEW);
  const [inventory, setInventory] = useState<CompanyInventoryItem[]>([]);
  const [rows, setRows] = useState<AcceptanceInspection[]>([]);
  const [detail, setDetail] = useState<AcceptanceInspection | null>(null);
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState<Record<number, string>>({});
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [inventoryFilter, setInventoryFilter] = useState<InventoryFilter>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const loadInventory = useCallback(async (keyword?: string) => {
    try {
      const list = await fetchCompanyInventory({ keyword });
      setInventory(list);
    } catch {
      reportError(setError, '재고 목록을 불러오지 못했습니다.');
    }
  }, []);

  const loadInspections = useCallback(
    async (filter?: InventoryFilter, keyword?: string) => {
      try {
        setLoading(true);
        const list = await fetchAcceptanceInspections({
          inventoryId: filter?.inventoryId,
          contractId: contractId ?? undefined,
          keyword,
        });
        setRows(list);
        setError('');
      } catch {
        reportError(setError, '인수검사 목록을 불러오지 못했습니다.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [contractId]
  );

  useEffect(() => {
    setView(LIST_VIEW);
    setInventoryFilter(null);
    setSearchKeyword('');
    void loadInventory();
    void loadInspections();
  }, [companyId, loadInventory, loadInspections, setView]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => {
      void loadInventory(searchKeyword);
      void loadInspections(inventoryFilter, searchKeyword);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, inventoryFilter, loadInventory, loadInspections, view.type]);

  useEffect(() => {
    if (view.type !== 'detail') {
      setDetail(null);
      setApprovalOpen(false);
      setApprovalMessage('');
      setAttachmentPreviewUrls((prev) => {
        Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });
      return;
    }
    let cancelled = false;
    const createdUrls: string[] = [];
    void (async () => {
      try {
        const data = await fetchAcceptanceInspection(view.inspectionId);
        if (cancelled) return;
        setDetail(data);
        setError('');

        const imageFiles = (data.attachments ?? []).filter(
          (file) => isMediaAttachment(file) && (file.contentType || '').startsWith('image/')
        );
        const urls: Record<number, string> = {};
        await Promise.all(
          imageFiles.map(async (file) => {
            try {
              const blob = await downloadAcceptanceInspectionAttachment(
                data.inspectionId,
                file.attachmentId
              );
              if (cancelled) return;
              const url = URL.createObjectURL(blob);
              createdUrls.push(url);
              urls[file.attachmentId] = url;
            } catch {
              // 미리보기 실패 시 파일명만 표시
            }
          })
        );
        if (!cancelled) {
          setAttachmentPreviewUrls(urls);
        }
      } catch {
        if (!cancelled) {
          reportError(setError, '인수검사 상세를 불러오지 못했습니다.');
          setDetail(null);
        }
      }
    })();
    return () => {
      cancelled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [view]);

  const goList = () => {
    setView(LIST_VIEW);
    void loadInventory(searchKeyword);
    void loadInspections(inventoryFilter, searchKeyword);
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      setError('');
      const name = acceptanceItemLabel(detail).replace(/[\\/:*?"<>|]+/g, '_');
      const lot = detail.lotNo?.trim() ? `_Lot${detail.lotNo.trim()}` : '';
      await downloadAcceptanceInspectionPdf(
        detail.inspectionId,
        `인수검사성적서_${name}${lot}.pdf`
      );
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.';
      notifyError(message);
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDelete = async (row: AcceptanceInspection) => {
    if (!window.confirm(`인수검사 #${row.inspectionId} 를 삭제할까요?`)) return;
    try {
      await deleteAcceptanceInspection(row.inspectionId);
      goList();
    } catch {
      notifyError('삭제에 실패했습니다.');
    }
  };

  const handleApproveDirect = async () => {
    if (!detail || detail.status === 'APPROVED') return;
    if (!canApproveDirect) {
      notifyError('바로 승인은 오너/팀장만 가능합니다.');
      return;
    }
    const ok = window.confirm(
      '인수검사를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없으며, 입고 수량이 회사 재고에 반영됩니다.'
    );
    if (!ok) return;
    try {
      setApproving(true);
      setError('');
      const updated = await approveAcceptanceInspection(detail.inspectionId);
      setDetail(updated);
      setApprovalMessage('바로 승인되었습니다. 회사 재고에 입고되었습니다.');
      setApprovalOpen(false);
      void loadInventory(searchKeyword);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '바로 승인에 실패했습니다.';
      notifyError(message);
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail || !detail.canRevoke) return;
    const ok = window.confirm(
      '승인을 철회할까요?\n승인란·검토란이 지워지고, 반영된 재고 입고도 되돌립니다.'
    );
    if (!ok) return;
    try {
      setApproving(true);
      setError('');
      const updated = await revokeAcceptanceInspection(detail.inspectionId);
      setDetail(updated);
      setApprovalMessage('승인이 철회되었습니다.');
      void loadInventory(searchKeyword);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '승인 철회에 실패했습니다.';
      notifyError(message);
    } finally {
      setApproving(false);
    }
  };

  const detailAttachmentsEditable =
    detail?.status === 'DRAFT' && !detail.approvalInProgress;

  const handleDetailAttachmentUpload = async (
    files: File[],
    kind: 'MEDIA' | 'REMARK'
  ) => {
    if (!detail || !detailAttachmentsEditable || files.length === 0) return;
    try {
      setLoading(true);
      for (const file of files) {
        await uploadAcceptanceInspectionAttachment(detail.inspectionId, file, kind);
      }
      setDetail(await fetchAcceptanceInspection(detail.inspectionId));
      void loadInspections(inventoryFilter, searchKeyword);
    } catch {
      reportError(setError, '첨부파일 업로드에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailAttachmentDelete = async (file: {
    attachmentId: number;
    fileName: string;
  }) => {
    if (!detail || !detailAttachmentsEditable) return;
    if (!window.confirm(`"${file.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setLoading(true);
      await deleteAcceptanceInspectionAttachment(detail.inspectionId, file.attachmentId);
      setDetail(await fetchAcceptanceInspection(detail.inspectionId));
      void loadInspections(inventoryFilter, searchKeyword);
    } catch {
      reportError(setError, '첨부파일 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="contract-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>인수검사</strong>
          <span>회사 재고</span>
        </div>

        <div className="contract-panel__search">
          <input
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="품명 / 규격 / Lot 검색"
          />
        </div>

        <div className="contract-panel__list-wrap">
          <ul className="contract-panel__list insp-side-list">
            <li>
              <button
                type="button"
                className={`contract-panel__list-item${
                  view.type === 'list' && inventoryFilter == null ? ' is-active' : ''
                }`}
                onClick={() => {
                  setInventoryFilter(null);
                  setView(LIST_VIEW);
                }}
              >
                전체 보기 ({rows.length})
              </button>
            </li>
            {inventory.map((item) => {
              const active = inventoryFilter?.inventoryId === item.inventoryId;
              return (
                <li key={item.inventoryId}>
                  <button
                    type="button"
                    className={`contract-panel__list-item${active ? ' is-active' : ''}`}
                    onClick={() => {
                      setInventoryFilter({
                        inventoryId: item.inventoryId,
                        itemName: item.itemName,
                        specText: item.specText ?? '',
                      });
                      setView(LIST_VIEW);
                    }}
                  >
                    <span className="contract-panel__list-name">{item.itemName}</span>
                    <span className="contract-panel__list-meta">
                      {item.specText ? `규격 ${item.specText} · ` : ''}
                      재고 {formatInventoryQty(item.stockQty)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      <section className="contract-panel__main" style={{ overflow: 'auto' }}>
        {view.type === 'create' || view.type === 'edit' ? (
          <AcceptanceInspectionCreate
            inspectionId={view.type === 'edit' ? view.inspectionId : undefined}
            contractId={contractId}
            onCancel={
              view.type === 'edit'
                ? () => setView({ type: 'detail', inspectionId: view.inspectionId })
                : goList
            }
            onSaved={(inspectionId) => setView({ type: 'detail', inspectionId })}
          />
        ) : view.type === 'detail' ? (
          <div className="equip-tab-panel" style={{ padding: 16 }}>
            <div className="equip-tab-panel__toolbar">
              <div>
                <h3>인수검사 상세</h3>
                <p className="equip-tab-panel__hint">
                  {detail ? acceptanceItemLabel(detail) : '-'} ·{' '}
                  {acceptanceStatusLabel(detail?.status, detail?.approvalInProgress)}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress ? (
                  <button
                    type="button"
                    onClick={() =>
                      setView({ type: 'edit', inspectionId: detail.inspectionId })
                    }
                  >
                    수정
                  </button>
                ) : null}
                {detail?.status === 'APPROVED' ? (
                  <button
                    type="button"
                    disabled={pdfDownloading}
                    onClick={() => void handleDownloadPdf()}
                  >
                    {pdfDownloading ? 'PDF 생성 중...' : 'PDF 다운로드'}
                  </button>
                ) : null}
                {detail?.canRevoke ? (
                  <button
                    type="button"
                    className="is-danger"
                    disabled={approving}
                    onClick={() => void handleRevoke()}
                  >
                    {approving ? '철회 중...' : '승인 철회'}
                  </button>
                ) : null}
                {detail?.canCancelApproval ? (
                  <ApprovalCancelButton
                    detail={detail}
                    className="is-ghost"
                    disabled={approving}
                    onCancelled={async () => {
                      if (!detail) return;
                      const next = await fetchAcceptanceInspection(detail.inspectionId);
                      setDetail(next);
                      setApprovalMessage('');
                      void loadInspections(inventoryFilter, searchKeyword);
                    }}
                  />
                ) : null}
                {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress ? (
                  <button type="button" onClick={() => setApprovalOpen(true)} disabled={approving}>
                    결재 요청
                  </button>
                ) : null}
                {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress && canApproveDirect ? (
                  <button
                    type="button"
                    onClick={() => void handleApproveDirect()}
                    disabled={approving}
                    style={{
                      background: '#2e7d32',
                      borderColor: '#2e7d32',
                      color: '#fff',
                    }}
                  >
                    {approving ? '승인 중...' : '승인'}
                  </button>
                ) : null}
                {detail && hasNonconformance(detail.overallJudgment, detail.items) ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate('/app', {
                        state: {
                          activeMenu: PRODUCT_SUB_NCR,
                          ncrPrefill: {
                            sourceType: 'ACCEPTANCE',
                            sourceInspectionId: detail.inspectionId,
                            inventoryId: detail.inventoryId ?? null,
                            itemName: acceptanceItemLabel(detail),
                            specText: detail.specText || '',
                            lotNo: detail.lotNo || '',
                            qty: detail.incomingQty || '',
                            reporterName: detail.inspectorName || detail.authorName || '',
                            reportDate: detail.inspectionDate?.slice(0, 10) || undefined,
                            defectDesc: buildNcrDefectDesc({
                              sourceLabel: '인수검사',
                              inspectionId: detail.inspectionId,
                              overallJudgment: detail.overallJudgment,
                              items: detail.items,
                              remark: detail.remark,
                            }),
                          },
                        },
                      })
                    }
                  >
                    부적합품 보고서
                  </button>
                ) : null}
                {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress ? (
                  <button
                    type="button"
                    className="is-danger"
                    onClick={() => void handleDelete(detail)}
                  >
                    삭제
                  </button>
                ) : null}
                <button type="button" className="is-ghost" onClick={goList}>
                  목록
                </button>
              </div>
            </div>
            {approvalMessage ? (
              <p className="equip-tab-panel__hint" style={{ color: '#1a5fb4' }}>
                {approvalMessage}
              </p>
            ) : null}
            {detail?.approvalInProgress ? (
              <p className="equip-tab-panel__hint">
                결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
              </p>
            ) : null}
            {error && <p className="equip-tab-panel__error">{error}</p>}
            {!detail ? (
              <p className="equip-tab-panel__hint">불러오는 중...</p>
            ) : (
              <>
                <AcceptanceInspectionSheet
                  values={{
                    inventoryId: detail.inventoryId ?? null,
                    itemKind: detail.itemKind || '',
                    productName: acceptanceItemLabel(detail) === '-' ? '' : acceptanceItemLabel(detail),
                    authorName: detail.authorName || '',
                    reviewerName: detail.reviewerName || '',
                    approverName: detail.approverName || '',
                    specText: detail.specText || '',
                    lotNo: detail.lotNo || '',
                    material: detail.material || '',
                    incomingQty: detail.incomingQty || '',
                    inspectorName: detail.inspectorName || '',
                    vendorName: detail.vendorName || '',
                    vendorContactName: detail.vendorContactName || '',
                    vendorPhone: detail.vendorPhone || '',
                    incomingDate: detail.incomingDate?.slice(0, 10) || '',
                    inspectionDate: detail.inspectionDate?.slice(0, 10) || '',
                    overallJudgment: detail.overallJudgment || '',
                    remark: detail.remark || '',
                    items: detail.items ?? [],
                  }}
                  mediaAttachments={(detail.attachments ?? []).filter(isMediaAttachment)}
                  remarkAttachments={(detail.attachments ?? []).filter(isRemarkAttachment)}
                  mediaPreviewUrls={attachmentPreviewUrls}
                  attachmentsEditable={detailAttachmentsEditable}
                  onPickMediaFiles={(files) => void handleDetailAttachmentUpload(files, 'MEDIA')}
                  onPickRemarkFiles={(files) => void handleDetailAttachmentUpload(files, 'REMARK')}
                  onDeleteAttachment={(file) => void handleDetailAttachmentDelete(file)}
                  onDownloadAttachment={(file) => {
                    void (async () => {
                      const blob = await downloadAcceptanceInspectionAttachment(
                        detail.inspectionId,
                        file.attachmentId
                      );
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = file.fileName;
                      a.click();
                      URL.revokeObjectURL(url);
                    })();
                  }}
                />
                <div
                  className="equip-tab-panel__toolbar"
                  style={{ marginTop: 16, justifyContent: 'flex-end' }}
                >
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress ? (
                      <button
                        type="button"
                        onClick={() =>
                          setView({ type: 'edit', inspectionId: detail.inspectionId })
                        }
                      >
                        수정
                      </button>
                    ) : null}
                    {detail?.status === 'APPROVED' ? (
                      <button
                        type="button"
                        disabled={pdfDownloading}
                        onClick={() => void handleDownloadPdf()}
                      >
                        {pdfDownloading ? 'PDF 생성 중...' : 'PDF 다운로드'}
                      </button>
                    ) : null}
                    {detail?.canRevoke ? (
                      <button
                        type="button"
                        className="is-danger"
                        disabled={approving}
                        onClick={() => void handleRevoke()}
                      >
                        {approving ? '철회 중...' : '승인 철회'}
                      </button>
                    ) : null}
                    {detail?.canCancelApproval ? (
                      <ApprovalCancelButton
                        detail={detail}
                        className="is-ghost"
                        disabled={approving}
                        onCancelled={async () => {
                          if (!detail) return;
                          const next = await fetchAcceptanceInspection(detail.inspectionId);
                          setDetail(next);
                          setApprovalMessage('');
                          void loadInspections(inventoryFilter, searchKeyword);
                        }}
                      />
                    ) : null}
                    {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress ? (
                      <button type="button" onClick={() => setApprovalOpen(true)} disabled={approving}>
                        결재 요청
                      </button>
                    ) : null}
                    {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress && canApproveDirect ? (
                      <button
                        type="button"
                        onClick={() => void handleApproveDirect()}
                        disabled={approving}
                        style={{
                          background: '#2e7d32',
                          borderColor: '#2e7d32',
                          color: '#fff',
                        }}
                      >
                        {approving ? '승인 중...' : '승인'}
                      </button>
                    ) : null}
                    {detail && hasNonconformance(detail.overallJudgment, detail.items) ? (
                      <button
                        type="button"
                        onClick={() =>
                          navigate('/app', {
                            state: {
                              activeMenu: PRODUCT_SUB_NCR,
                              ncrPrefill: {
                                sourceType: 'ACCEPTANCE',
                                sourceInspectionId: detail.inspectionId,
                                inventoryId: detail.inventoryId ?? null,
                                itemName: acceptanceItemLabel(detail),
                                specText: detail.specText || '',
                                lotNo: detail.lotNo || '',
                                qty: detail.incomingQty || '',
                                reporterName: detail.inspectorName || detail.authorName || '',
                                reportDate: detail.inspectionDate?.slice(0, 10) || undefined,
                                defectDesc: buildNcrDefectDesc({
                                  sourceLabel: '인수검사',
                                  inspectionId: detail.inspectionId,
                                  overallJudgment: detail.overallJudgment,
                                  items: detail.items,
                                  remark: detail.remark,
                                }),
                              },
                            },
                          })
                        }
                      >
                        부적합품 보고서
                      </button>
                    ) : null}
                    {detail && detail.status !== 'APPROVED' && !detail.approvalInProgress ? (
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => void handleDelete(detail)}
                      >
                        삭제
                      </button>
                    ) : null}
                    <button type="button" className="is-ghost" onClick={goList}>
                      목록
                    </button>
                  </div>
                </div>
                <ApprovalRequestModal
                  open={approvalOpen}
                  titleDefault={`${acceptanceItemLabel(detail)} 인수검사성적서 결재`}
                  targetType={APPROVAL_TARGET_ACCEPTANCE_INSPECTION}
                  targetId={detail.inspectionId}
                  onClose={() => setApprovalOpen(false)}
                  onSubmitted={() => {
                    setApprovalOpen(false);
                    setApprovalMessage(
                      '결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.'
                    );
                    void fetchAcceptanceInspection(detail.inspectionId).then(setDetail);
                    void loadInspections(inventoryFilter, searchKeyword);
                  }}
                />
              </>
            )}
          </div>
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>{contractId != null ? '계약 인수검사' : '인수검사 현황'}</h2>
                <p>
                  {inventoryFilter
                    ? `선택: ${inventoryFilter.itemName}${
                        inventoryFilter.specText ? ` · ${inventoryFilter.specText}` : ''
                      }`
                    : contractId != null
                      ? `이 계약에 연결된 인수검사 ${rows.length}건`
                      : `표시 ${rows.length}건 · 재고 품목 선택 후 등록 · 승인 전 수정 가능 · 승인 시 입고`}
                </p>
              </div>
              <button
                type="button"
                className="contract-panel__primary"
                onClick={() => setView({ type: 'create' })}
              >
                인수검사 등록
              </button>
            </div>

            {error && (
              <p className="equip-tab-panel__error" style={{ padding: '0 14px' }}>
                {error}
              </p>
            )}

            <div className="contract-panel__table-wrap">
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : rows.length === 0 ? (
                <p className="contract-panel__hint">등록된 인수검사가 없습니다.</p>
              ) : (
                <table className="ec-table insp-list-table">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>No</th>
                      <th>품명</th>
                      <th style={{ width: 100 }}>규격</th>
                      <th style={{ width: 110 }}>양식</th>
                      <th style={{ width: 168 }}>Lot</th>
                      <th style={{ width: 72 }}>수량</th>
                      <th style={{ width: 88 }}>검사자</th>
                      <th style={{ width: 100 }}>검사일</th>
                      <th style={{ width: 88 }}>상태</th>
                      <th style={{ width: 72 }}>판정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={row.inspectionId}
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          setView({ type: 'detail', inspectionId: row.inspectionId })
                        }
                      >
                        <td>{rows.length - index}</td>
                        <td title={acceptanceItemLabel(row)}>{acceptanceItemLabel(row)}</td>
                        <td title={row.specText || undefined}>{row.specText || '-'}</td>
                        <td title={row.formTitle || undefined}>{row.formTitle || '-'}</td>
                        <td title={row.lotNo || undefined}>{row.lotNo || '-'}</td>
                        <td>{row.incomingQty || '-'}</td>
                        <td title={row.inspectorName || undefined}>{row.inspectorName || '-'}</td>
                        <td>{formatDate(row.inspectionDate)}</td>
                        <td>{acceptanceStatusLabel(row.status, row.approvalInProgress)}</td>
                        <td>{row.overallJudgment || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default AcceptanceInspectionPanel;
